import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { PlusCircle, UserPlus } from "lucide-react";
import type { ValidationIssue } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { ChoiceSelect, ChoiceCombobox, ChoiceMultiSelect } from "@/components/forms";
import {
  applyCerToMovimentoForm,
  cerEntriesToChoices,
  findCerEntry,
  useCerAttiviList,
} from "@/lib/cerAttiviChoices";
import {
  contactsToChoices,
  useOperatoriByRole,
  type OperatoreRuolo,
} from "@/lib/operatoriChoices";
import {
  CAUSALI_OPERAZIONE_FALLBACK,
  PROVENIENZA,
  STATO_FISICO,
  TIPO_MOVIMENTO,
  UNITA_MISURA_FORM,
  useRentriChoices,
  type RentriChoice,
} from "@/lib/rentriChoices";
import type { MovimentoFormState } from "@/lib/movimentoForm";

const TABS = ["Generali", "Rifiuto", "FIR", "Esito", "Soggetti"] as const;

export function MovimentoFormPanel({
  form,
  onChange,
  issues = [],
  className,
}: {
  form: MovimentoFormState;
  onChange: (next: MovimentoFormState) => void;
  issues?: ValidationIssue[];
  className?: string;
}) {
  const [tab, setTab] = useState(0);

  const causaliQ = useRentriChoices("causali-operazione");
  const causali: RentriChoice[] =
    causaliQ.data && causaliQ.data.length > 0 ? causaliQ.data : CAUSALI_OPERAZIONE_FALLBACK;

  const hpQ = useRentriChoices("caratteristiche-pericolo");
  const hpOptions: RentriChoice[] = hpQ.data ?? [];

  const attivitaQ = useRentriChoices("attivita-rs");
  const attivitaOptions: RentriChoice[] = attivitaQ.data ?? [];

  const cerQ = useCerAttiviList();
  const cerItems = cerQ.data?.items ?? [];
  const eerOptions = cerEntriesToChoices(cerItems);

  const prodQ = useOperatoriByRole("produttore");
  const trasQ = useOperatoriByRole("trasportatore");
  const destQ = useOperatoriByRole("destinatario");
  const prodOptions = contactsToChoices(
    Array.isArray(prodQ.data) ? prodQ.data : [],
  );
  const trasOptions = contactsToChoices(
    Array.isArray(trasQ.data) ? trasQ.data : [],
  );
  const destOptions = contactsToChoices(
    Array.isArray(destQ.data) ? destQ.data : [],
  );

  const set = <K extends keyof MovimentoFormState>(k: K, v: MovimentoFormState[K]) =>
    onChange({ ...form, [k]: v });

  return (
    <div className={className}>
      <div className="flex shrink-0 gap-1 border-b pb-2">
        {TABS.map((label, i) => (
          <button
            key={label}
            type="button"
            onClick={() => setTab(i)}
            className={cn(
              "rounded px-2 py-1 text-xs font-medium",
              tab === i
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:bg-accent",
            )}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto py-3">
        {tab === 0 && (
          <>
            <Field label="Data registrazione">
              <Input type="date" value={form.data} onChange={(e) => set("data", e.target.value)} />
            </Field>
            <Field label="Tipo">
              <ChoiceSelect
                value={form.tipo}
                onChange={(v) => set("tipo", (v || "Carico") as "Carico" | "Scarico")}
                options={TIPO_MOVIMENTO}
                required
              />
            </Field>
            <Field label="Causale operazione">
              <ChoiceSelect
                value={form.causale_operazione}
                onChange={(v) => set("causale_operazione", v)}
                options={causali}
                placeholder="— Seleziona causale —"
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Anno (opz.)">
                <Input value={form.anno} onChange={(e) => set("anno", e.target.value)} />
              </Field>
              <Field label="Progressivo (opz.)">
                <Input
                  value={form.progressivo}
                  onChange={(e) => set("progressivo", e.target.value)}
                />
              </Field>
            </div>
          </>
        )}
        {tab === 1 && (
          <>
            {cerItems.length === 0 && !cerQ.isLoading && (
              <div className="rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/30 p-3 text-sm text-amber-800 dark:text-amber-300 space-y-2">
                <p>
                  Nessun CER attivo configurato. I movimenti usano solo i
                  codici presenti in <strong>CER attivi</strong>.
                </p>
                <Button variant="outline" size="sm" asChild>
                  <Link to="/cer-attivi">
                    <PlusCircle className="mr-1.5 h-4 w-4" /> Aggiungi CER attivo
                  </Link>
                </Button>
              </div>
            )}
            <Field label="Codice CER (da CER attivi)">
              <ChoiceCombobox
                value={form.eer}
                onChange={(v) => {
                  const entry = findCerEntry(cerItems, v);
                  onChange(
                    entry ? applyCerToMovimentoForm(form, entry) : { ...form, eer: v },
                  );
                }}
                options={eerOptions}
                loading={cerQ.isLoading}
                formatValue={(v) => {
                  const clean = v.replace(/\./g, "");
                  return clean.length === 6
                    ? `${clean.slice(0, 2)}.${clean.slice(2, 4)}.${clean.slice(4)}`
                    : clean;
                }}
              />
            </Field>
            <Field label="Descrizione">
              <Input
                value={form.descrizione_eer}
                onChange={(e) => set("descrizione_eer", e.target.value)}
              />
            </Field>
            <Field label="Provenienza">
              <ChoiceSelect
                value={form.provenienza}
                onChange={(v) => set("provenienza", v)}
                options={PROVENIENZA}
                required
              />
            </Field>
            <Field label="Stato fisico">
              <ChoiceSelect
                value={form.stato_fisico}
                onChange={(v) => set("stato_fisico", v)}
                options={STATO_FISICO}
                required
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Quantità">
                <Input value={form.quantita} onChange={(e) => set("quantita", e.target.value)} />
              </Field>
              <Field label="Unità di misura">
                <ChoiceSelect
                  value={form.unita_misura}
                  onChange={(v) => set("unita_misura", v)}
                  options={UNITA_MISURA_FORM}
                  required
                />
              </Field>
            </div>
            <Field label="Caratteristiche di pericolo (HP)">
              <ChoiceMultiSelect
                values={form.caratteristiche_pericolo}
                onChange={(vs) => set("caratteristiche_pericolo", vs)}
                options={hpOptions}
                loading={hpQ.isLoading}
                emptyText="Nessuna caratteristica HP disponibile"
              />
            </Field>
            <Field label="Destinato ad attività">
              <ChoiceCombobox
                value={form.destinato_attivita}
                onChange={(v) => set("destinato_attivita", v)}
                options={attivitaOptions}
                loading={attivitaQ.isLoading}
                placeholder="Cerca attività R/D…"
              />
            </Field>
            <Field label="Annotazioni">
              <Input value={form.annotazioni} onChange={(e) => set("annotazioni", e.target.value)} />
            </Field>
          </>
        )}
        {tab === 2 && (
          <>
            <Field label="N. FIR collegato">
              <Input value={form.numero_fir} onChange={(e) => set("numero_fir", e.target.value)} />
            </Field>
            <Field label="Data inizio trasporto">
              <Input
                type="date"
                value={form.data_inizio_trasporto}
                onChange={(e) => set("data_inizio_trasporto", e.target.value)}
              />
            </Field>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.trasporto_transfrontaliero}
                onChange={(e) => set("trasporto_transfrontaliero", e.target.checked)}
              />
              Trasporto transfrontaliero
            </label>
          </>
        )}
        {tab === 3 && (
          <>
            <Field label="Peso verificato a destino (kg)">
              <Input
                value={form.peso_verificato_destino}
                onChange={(e) => set("peso_verificato_destino", e.target.value)}
              />
            </Field>
            <Field label="Data fine trasporto">
              <Input
                type="date"
                value={form.data_fine_trasporto}
                onChange={(e) => set("data_fine_trasporto", e.target.value)}
              />
            </Field>
          </>
        )}
        {tab === 4 && (
          <>
            <p className="text-xs text-muted-foreground">
              Seleziona operatori dalla rubrica. Aggiungili in{" "}
              <Link to="/contacts" className="underline font-medium">
                Operatori
              </Link>
              .
            </p>
            <OperatoreField
              label="Produttore"
              ruolo="produttore"
              value={form.produttore_cf}
              options={prodOptions}
              loading={prodQ.isLoading}
              empty={prodOptions.length === 0}
              onChange={(v) => set("produttore_cf", v)}
            />
            <OperatoreField
              label="Trasportatore"
              ruolo="trasportatore"
              value={form.trasportatore_cf}
              options={trasOptions}
              loading={trasQ.isLoading}
              empty={trasOptions.length === 0}
              onChange={(v) => set("trasportatore_cf", v)}
            />
            <OperatoreField
              label="Destinatario"
              ruolo="destinatario"
              value={form.destinatario_cf}
              options={destOptions}
              loading={destQ.isLoading}
              empty={destOptions.length === 0}
              onChange={(v) => set("destinatario_cf", v)}
            />
          </>
        )}
        {issues.length > 0 && (
          <div className="rounded border border-destructive/30 bg-destructive/5 p-2 text-xs">
            {issues.map((i, idx) => (
              <div key={idx} className="mb-1">
                <Badge variant={i.severity === "error" ? "destructive" : "secondary"}>
                  {i.severity}
                </Badge>{" "}
                {i.message}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function OperatoreField({
  label,
  ruolo,
  value,
  options,
  loading,
  empty,
  onChange,
}: {
  label: string;
  ruolo: OperatoreRuolo;
  value: string;
  options: RentriChoice[];
  loading: boolean;
  empty: boolean;
  onChange: (cf: string) => void;
}) {
  return (
    <div className="space-y-1">
      <Field label={label}>
        <ChoiceCombobox
          value={value}
          onChange={onChange}
          options={options}
          loading={loading}
          placeholder={`Seleziona ${label.toLowerCase()}…`}
        />
      </Field>
      {empty && !loading && (
        <Button variant="link" size="sm" className="h-auto p-0 text-xs" asChild>
          <Link to={`/contacts?ruolo=${ruolo}&nuovo=1`}>
            <UserPlus className="mr-1 h-3 w-3" /> Aggiungi {label.toLowerCase()}
          </Link>
        </Button>
      )}
    </div>
  );
}
